import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { NewsCard } from "@/components/NewsCard";
import { AdBanner } from "@/components/AdBanner";
import { Article, categories } from "@shared/schema";
import { Loader2 } from "lucide-react";
import { useRegion } from "@/contexts/RegionContext";
import { PageContainer } from "@/components/layout/PageContainer";

export default function Category() {
  const { id } = useParams<{ id: string }>();
  const { selectedRegion } = useRegion();

  const { data: articles, isLoading } = useQuery<Article[]>({
    queryKey: ["/api/articles/category", id, selectedRegion],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedRegion && selectedRegion !== "ALL") {
        params.append("region", selectedRegion);
      }
      const url = `/api/articles/category/${id}${params.toString() ? `?${params}` : ""}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch articles");
      return response.json();
    },
  });

  const category = categories.find((c) => c.id === id);
  const filteredArticles = articles || []; // Already filtered by API

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <PageContainer size="wide">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            {category?.label || id}
          </h1>
          <p className="text-muted-foreground">
            {filteredArticles.length}개의 기사
          </p>
        </div>

        {/* Ad Banner */}
        <AdBanner size="header" className="mb-8" />

        {/* Articles Grid */}
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground">
              아직 이 카테고리에 기사가 없습니다
            </p>
          </div>
        )}
    </PageContainer>
  );
}
